import '../global.css';
import { useEffect, useRef, useState } from 'react';
import { AppState, Platform, View } from 'react-native';
import { Slot, useGlobalSearchParams, usePathname, useRouter, useSegments } from 'expo-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import * as Updates from 'expo-updates';
import { useFonts } from 'expo-font';
import {
  DMMono_400Regular,
  DMMono_500Medium,
} from '@expo-google-fonts/dm-mono';
import {
  InstrumentSerif_400Regular,
} from '@expo-google-fonts/instrument-serif';
import * as Notifications from '../lib/notifications';
import * as Sentry from '@sentry/react-native';
import {
  identifyAnalyticsUser,
  updateAnalyticsRoute,
} from '../lib/analytics';
import { getClientEventContext } from '../lib/client-event-context';
import { consumePendingMarketingAttribution } from '../lib/marketing-attribution';
import {
  trackAppInstallFirstSeen,
  trackNavigationChanged,
  trackAppSessionStarted,
} from '../lib/product-events';
import { useWebReleaseUpdate } from '../hooks/useWebReleaseUpdate';
import { getNotificationTargetRoute } from '../lib/notification-routing';
import { consumePendingAuthRedirect, normalizeAuthRedirect } from '../lib/auth-redirect';
import {
  initializeSupabaseAuth,
  isDemoSession,
  isLocalDevSession,
  supabase,
} from '../lib/supabase';
import { trpc, queryClient, createTRPCClientInstance } from '../lib/trpc';
import { configureRevenueCat } from '../lib/purchases';
import { ThemeProvider } from '../lib/theme';
import type { Session } from '@supabase/supabase-js';
import { AppUpdateBanner } from '../components/AppUpdateBanner';
import { ConfirmationProvider } from '../components/ConfirmationDialog';
import { InAppBannerProvider } from '../components/InAppBanner';
import { LegalTermsGate } from '../components/LegalTermsGate';
import { LoadingSpinner } from '../components/LoadingSpinner';

const DEFAULT_POST_AUTH_ROUTE = '/(app)' as const;
const UPDATE_CHECK_MIN_INTERVAL_MS = 5 * 60 * 1000;

function firstSearchParam(value: string | string[] | undefined): string | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

function getSessionUserId(session: Session | null): string | null {
  return session?.user.id ?? null;
}

function isPublicWebRoute(pathname: string | null | undefined): boolean {
  if (Platform.OS !== 'web') return false;
  if (!pathname) return false;

  return (
    pathname === '/' ||
    pathname === '/support' ||
    pathname === '/ai-connectors' ||
    pathname === '/oauth/consent' ||
    pathname === '/compare' ||
    pathname.startsWith('/compare/') ||
    pathname === '/guides' ||
    pathname.startsWith('/guides/') ||
    pathname.startsWith('/demo/')
  );
}

function isAuthenticatedAppGroup(group: string | undefined): boolean {
  return group === '(app)' || group === '(onboarding)';
}

/**
 * Root layout — auth guard + tRPC/React Query providers.
 *
 * Routing logic:
 *   - No session → /(auth)/login
 *   - Session → /(app)
 *   - The authenticated app shell redirects back to /(onboarding) when
 *     household.onboardingStep is still incomplete.
 */
function RootLayout() {
  const { isUpdatePending, isRestarting } = Updates.useUpdates();
  const [fontsLoaded] = useFonts({
    DMMono_400Regular,
    DMMono_500Medium,
    InstrumentSerif_400Regular,
  });
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdateBannerVisible, setIsUpdateBannerVisible] = useState(false);
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);
  const [trpcClient] = useState(() => createTRPCClientInstance());
  const { isWebUpdateAvailable, refreshWebApp, dismissWebUpdate } =
    useWebReleaseUpdate();
  const authRedirectStartedRef = useRef(false);
  const rootPostAuthRedirectStartedRef = useRef(false);
  const handledNotificationIdsRef = useRef<Set<string>>(new Set());
  const pendingNotificationRouteRef = useRef<ReturnType<
    typeof getNotificationTargetRoute
  > | null>(null);
  const reportedSessionUserIdRef = useRef<string | null>(null);
  const reportedNativeNavigationRouteRef = useRef<string | null>(null);
  const reportedTimezoneUserIdRef = useRef<string | null>(null);
  const reportedInstallRef = useRef(false);
  const appStateRef = useRef(AppState.currentState);
  const updateCheckInFlightRef = useRef(false);
  const lastUpdateCheckAtRef = useRef<number>(0);
  const mountedRef = useRef(false);
  const authSessionUserIdRef = useRef<string | null>(null);
  const nativeAutoRefreshSessionUserIdRef = useRef<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useGlobalSearchParams<{ redirect?: string | string[] }>();
  const segments = useSegments();
  const segmentsKey = segments.join('/');
  const currentGroup = segments[0];
  const requestedAuthRedirect = normalizeAuthRedirect(firstSearchParam(searchParams.redirect));

  const checkForDownloadedUpdate = async (reason: 'launch' | 'foreground') => {
    if (Platform.OS === 'web' || __DEV__ || !Updates.isEnabled) {
      return;
    }

    if (isUpdatePending || updateCheckInFlightRef.current) {
      if (isUpdatePending) {
        setIsUpdateBannerVisible(true);
      }
      return;
    }

    const now = Date.now();
    if (
      reason === 'foreground' &&
      now - lastUpdateCheckAtRef.current < UPDATE_CHECK_MIN_INTERVAL_MS
    ) {
      return;
    }

    updateCheckInFlightRef.current = true;
    lastUpdateCheckAtRef.current = now;

    try {
      const result = await Updates.checkForUpdateAsync();
      if (!result.isAvailable) return;

      await Updates.fetchUpdateAsync();
      setIsUpdateBannerVisible(true);
    } catch {
      // Ignore transient update lookup failures and fall back to Expo's
      // default startup update behavior.
    } finally {
      updateCheckInFlightRef.current = false;
    }
  };

  const applyDownloadedUpdate = async () => {
    if (Platform.OS === 'web' || __DEV__ || !Updates.isEnabled) {
      return;
    }

    setIsApplyingUpdate(true);

    try {
      await Updates.reloadAsync();
    } catch {
      setIsApplyingUpdate(false);
      setIsUpdateBannerVisible(true);
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    const { data: listener } = supabase.auth.onAuthStateChange((_event, s) => {
      const nextSessionUserId = getSessionUserId(s);
      if (authSessionUserIdRef.current !== nextSessionUserId) {
        queryClient.clear();
        authSessionUserIdRef.current = nextSessionUserId;
      }

      if (__DEV__ && _event !== 'TOKEN_REFRESHED') {
        console.log('[auth] onAuthStateChange', {
          event: _event,
          userId: nextSessionUserId,
          email: s?.user.email ?? null,
          identities: s?.user.identities?.map((identity) => identity.provider) ?? [],
        });
      }

      if (Platform.OS !== 'web') {
        if (s && nativeAutoRefreshSessionUserIdRef.current !== nextSessionUserId) {
          nativeAutoRefreshSessionUserIdRef.current = nextSessionUserId;
          void supabase.auth.startAutoRefresh();
        } else if (!s && nativeAutoRefreshSessionUserIdRef.current !== null) {
          nativeAutoRefreshSessionUserIdRef.current = null;
          void supabase.auth.stopAutoRefresh();
        }
      }

      if (s && !isDemoSession(s) && !isLocalDevSession(s)) {
        configureRevenueCat(s.user.id);
      }
      if (mountedRef.current) setSession(s);
    });

    initializeSupabaseAuth()
      .then(() => supabase.auth.getSession())
      .then(({ data }) => {
        authSessionUserIdRef.current = getSessionUserId(data.session);

        if (__DEV__) {
          console.log('[auth] getSession resolved', {
            userId: authSessionUserIdRef.current,
            email: data.session?.user.email ?? null,
          });
        }

        if (data.session && !isDemoSession(data.session) && !isLocalDevSession(data.session)) {
          configureRevenueCat(data.session.user.id);
        }
        if (mountedRef.current) setSession(data.session);
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });

    return () => {
      mountedRef.current = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (isLoading) return;

    const inAuthGroup = currentGroup === '(auth)';
    const canStayOnPublicWebRoute = isPublicWebRoute(pathname);
    const canStayInAuthGroup = pathname === '/callback' || pathname === '/invite';
    const shouldHandleRootPostAuthRedirect = Boolean(
      session &&
      Platform.OS === 'web' &&
      pathname === '/' &&
      currentGroup == null,
    );

    if (!session || !inAuthGroup || canStayInAuthGroup) {
      authRedirectStartedRef.current = false;
    }
    if (!shouldHandleRootPostAuthRedirect) {
      rootPostAuthRedirectStartedRef.current = false;
    }

    if (!session && !inAuthGroup && !canStayOnPublicWebRoute) {
      router.replace('/(auth)/login');
    } else if (shouldHandleRootPostAuthRedirect) {
      if (rootPostAuthRedirectStartedRef.current) return;
      rootPostAuthRedirectStartedRef.current = true;

      let cancelled = false;

      void (async () => {
        const redirect = await consumePendingAuthRedirect();
        if (cancelled) return;
        router.replace((redirect ?? DEFAULT_POST_AUTH_ROUTE) as any);
      })();

      return () => {
        cancelled = true;
      };
    } else if (
      session &&
      inAuthGroup &&
      !canStayInAuthGroup &&
      !canStayOnPublicWebRoute
    ) {
      if (authRedirectStartedRef.current) return;
      authRedirectStartedRef.current = true;

      let cancelled = false;

      void (async () => {
        const pendingRedirect = await consumePendingAuthRedirect();
        if (cancelled) return;
        const redirect = requestedAuthRedirect ?? pendingRedirect;
        router.replace((redirect ?? DEFAULT_POST_AUTH_ROUTE) as any);
      })();

      return () => {
        cancelled = true;
      };
    }
  }, [currentGroup, isLoading, pathname, requestedAuthRedirect, router, session]);

  useEffect(() => {
    if (!isUpdatePending) return;
    setIsUpdateBannerVisible(true);
  }, [isUpdatePending]);

  useEffect(() => {
    if (isLoading) return;
    void checkForDownloadedUpdate('launch');
  }, [isLoading]);

  useEffect(() => {
    if (Platform.OS === 'web' || isLoading) return;

    const subscription = AppState.addEventListener('change', (nextState) => {
      const previousState = appStateRef.current;
      appStateRef.current = nextState;

      if (
        (previousState === 'background' || previousState === 'inactive') &&
        nextState === 'active'
      ) {
        void checkForDownloadedUpdate('foreground');
      }
    });

    return () => subscription.remove();
  }, [isLoading]);

  useEffect(() => {
    if (Platform.OS === 'web' || isLoading || !session) return;

    const extractRoute = (
      response: Notifications.NotificationResponse | null,
    ) => {
      if (!response) return null;

      const notificationId = response.notification.request.identifier;
      if (handledNotificationIdsRef.current.has(notificationId)) return null;
      handledNotificationIdsRef.current.add(notificationId);

      const data = response.notification.request.content.data;
      return getNotificationTargetRoute(
        data as Record<string, unknown> | undefined,
      );
    };

    // Cold-start: defer routing until the initial Redirect has landed us
    // inside (app). Pushing immediately races the Redirect's router.replace
    // and can leave the navigator oscillating ("infinite flicker"). The
    // companion effect below flushes pendingNotificationRouteRef once
    // currentGroup === '(app)'.
    Notifications.getLastNotificationResponseAsync()
      .then((response) => {
        const route = extractRoute(response);
        if (route) pendingNotificationRouteRef.current = route;
      })
      .catch(() => {
        // Ignore launch-response read failures in development.
      });

    // Foreground / background tap: nav tree is already mounted, push directly.
    const subscription = Notifications.addNotificationResponseReceivedListener(
      (response) => {
        const route = extractRoute(response);
        if (route) router.push(route);
      },
    );

    return () => subscription.remove();
  }, [isLoading, router, session]);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (currentGroup !== '(app)') return;
    const pending = pendingNotificationRouteRef.current;
    if (!pending) return;
    pendingNotificationRouteRef.current = null;
    router.push(pending);
  }, [currentGroup, router]);

  useEffect(() => {
    identifyAnalyticsUser(session);
  }, [session]);

  useEffect(() => {
    updateAnalyticsRoute(pathname);
  }, [pathname]);

  useEffect(() => {
    if (Platform.OS === 'web' || isLoading) return;

    const route = pathname ?? (segmentsKey ? `/${segmentsKey}` : '/');
    const previousRoute = reportedNativeNavigationRouteRef.current;
    if (previousRoute === route) return;

    reportedNativeNavigationRouteRef.current = route;

    trackNavigationChanged({
      route,
      previousRoute,
      currentGroup: currentGroup ?? 'root',
      authState: session ? 'authenticated' : 'anonymous',
      metadata: {
        isDemo: session ? isDemoSession(session) : false,
        segmentCount: segments.length,
      },
    });
  }, [currentGroup, isLoading, pathname, segments.length, segmentsKey, session]);

  useEffect(() => {
    if (isLoading || reportedInstallRef.current || Platform.OS === 'web') {
      return;
    }

    let cancelled = false;

    void (async () => {
      const context = await getClientEventContext();
      if (cancelled) return;
      if (context['isFirstSessionOnDevice'] !== true) return;

      reportedInstallRef.current = true;
      trackAppInstallFirstSeen({
        route: pathname,
        metadata: {
          authState: session ? 'authenticated' : 'anonymous',
          currentGroup: currentGroup ?? 'root',
        },
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [currentGroup, isLoading, pathname, session]);

  // Push the client-detected IANA timezone to the API once per authenticated
  // session so per-diem day boundaries follow the user. Failures are silent —
  // the API defaults to America/New_York if no value is ever stored.
  useEffect(() => {
    if (isLoading || !session) {
      reportedTimezoneUserIdRef.current = null;
      return;
    }
    if (currentGroup === '(auth)') return;
    const userId = session.user.id;
    if (reportedTimezoneUserIdRef.current === userId) return;

    let timezone: string | null = null;
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      timezone = null;
    }
    if (!timezone) return;

    reportedTimezoneUserIdRef.current = userId;
    trpcClient.households.updateTimezone
      .mutate({ timezone })
      .catch(() => {
        reportedTimezoneUserIdRef.current = null;
      });
  }, [currentGroup, isLoading, session, trpcClient]);

  useEffect(() => {
    if (!session) {
      reportedSessionUserIdRef.current = null;
      return;
    }

    if (isLoading || currentGroup === '(auth)') {
      return;
    }

    const sessionUserId = session.user.id;
    if (reportedSessionUserIdRef.current === sessionUserId) {
      return;
    }

    reportedSessionUserIdRef.current = sessionUserId;

    void (async () => {
      const marketingAttribution = await consumePendingMarketingAttribution();

      trackAppSessionStarted({
        route: pathname,
        metadata: {
          currentGroup: currentGroup ?? 'root',
          isDemo: isDemoSession(session),
          marketingSource: marketingAttribution?.source ?? null,
          marketingPageType: marketingAttribution?.pageType ?? null,
          marketingPageSlug: marketingAttribution?.pageSlug ?? null,
          marketingArticleSlug: marketingAttribution?.articleSlug ?? null,
          marketingCtaId: marketingAttribution?.ctaId ?? null,
        },
      });
    })();
  }, [currentGroup, isLoading, pathname, session]);

  const canShowUpdateBanner =
    !!session && isAuthenticatedAppGroup(currentGroup);
  const isWebUpdateBannerVisible =
    canShowUpdateBanner && Platform.OS === 'web' && isWebUpdateAvailable;
  const isNativeUpdateBannerVisible =
    canShowUpdateBanner && isUpdateBannerVisible;

  if (isLoading || !fontsLoaded) return <LoadingSpinner />;

  return (
    <ThemeProvider>
      <SafeAreaProvider>
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <ThemeRoot
              isUpdateBannerVisible={
                isNativeUpdateBannerVisible || isWebUpdateBannerVisible
              }
              isApplyingUpdate={
                isWebUpdateBannerVisible
                  ? false
                  : isApplyingUpdate || isRestarting
              }
              onApplyUpdate={() => {
                if (isWebUpdateBannerVisible) {
                  refreshWebApp();
                  return;
                }
                void applyDownloadedUpdate();
              }}
              onDismissUpdate={() => {
                if (isWebUpdateBannerVisible) {
                  dismissWebUpdate();
                  return;
                }
                setIsUpdateBannerVisible(false);
              }}
              updateBannerVariant={isWebUpdateBannerVisible ? 'web' : 'native'}
              showLegalTermsGate={!!session && currentGroup !== '(auth)'}
            />
          </QueryClientProvider>
        </trpc.Provider>
      </SafeAreaProvider>
    </ThemeProvider>
  );
}

interface ThemeRootProps {
  isUpdateBannerVisible: boolean;
  isApplyingUpdate: boolean;
  onApplyUpdate: () => void;
  onDismissUpdate: () => void;
  updateBannerVariant: 'native' | 'web';
  showLegalTermsGate: boolean;
}

function ThemeRoot({
  isUpdateBannerVisible,
  isApplyingUpdate,
  onApplyUpdate,
  onDismissUpdate,
  updateBannerVariant,
  showLegalTermsGate,
}: ThemeRootProps) {
  return (
    <ConfirmationProvider>
      <InAppBannerProvider>
        <View className="flex-1">
          <Slot />
          <AppUpdateBanner
            visible={isUpdateBannerVisible}
            onApply={onApplyUpdate}
            onDismiss={onDismissUpdate}
            applying={isApplyingUpdate}
            variant={updateBannerVariant}
          />
          <LegalTermsGate enabled={showLegalTermsGate} />
        </View>
      </InAppBannerProvider>
    </ConfirmationProvider>
  );
}

export default Sentry.wrap(RootLayout);
